import { useMutation, useQueryClient } from "@tanstack/react-query";
import { CalendarClock, Clock, FileText, Mail, Phone, StickyNote, type LucideIcon } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { del, patch, post } from "@/lib/api";
import { date, eur, hours, today } from "@/lib/format";
import { groupByDate, relativeDays } from "@/lib/relative";
import type { Contact, Note, NoteKind, TimelineItem } from "@/lib/types";
import { notePayload } from "./composer";
import { heyLink, mailMeta } from "./mail";

const KIND: Record<NoteKind, { label: string; icon: LucideIcon }> = {
  note: { label: "Notiz", icon: StickyNote },
  call: { label: "Anruf", icon: Phone },
  meeting: { label: "Termin", icon: CalendarClock },
};

function Composer({ companyId, contacts }: { companyId: number; contacts: Contact[] }) {
  const qc = useQueryClient();
  const [kind, setKind] = useState<NoteKind>("note");
  // Unset = "today" as the server sees it at save time; the field shows today's date (read at render, so it
  // never goes stale overnight) and only a date the user picked is sent.
  const [day, setDay] = useState("");
  const [contactId, setContactId] = useState("none");
  const [body, setBody] = useState("");
  const add = useMutation({
    mutationFn: () => post<Note>(`/api/companies/${companyId}/notes`, notePayload(kind, body, day, contactId)),
    onSuccess: () => {
      setBody("");
      setDay("");
      qc.invalidateQueries({ queryKey: ["company", companyId] });
      qc.invalidateQueries({ queryKey: ["companies"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
    },
    onError: (e) => toast.error(e.message),
  });
  const active = contacts.filter((k) => !k.archived_at);
  return (
    <form className="space-y-2 rounded-xl p-3 ring-1 ring-border" onSubmit={(e) => { e.preventDefault(); if (body.trim() && !add.isPending) add.mutate(); }}>
      <div className="flex flex-wrap items-center gap-2">
        <Tabs value={kind} onValueChange={(v) => setKind(v as NoteKind)}>
          <TabsList aria-label="Art">{(Object.keys(KIND) as NoteKind[]).map((k) => <TabsTrigger key={k} value={k}>{KIND[k].label}</TabsTrigger>)}</TabsList>
        </Tabs>
        <Input type="date" aria-label="Datum" className="h-8 w-40" value={day || today()} max={today()} onChange={(e) => setDay(e.target.value === today() ? "" : e.target.value)} />
        {active.length > 0 && (
          <Select value={contactId} onValueChange={setContactId}>
            <SelectTrigger className="h-8 w-44" aria-label="Kontakt"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">ohne Kontakt</SelectItem>
              {active.map((k) => <SelectItem key={k.id} value={String(k.id)}>{k.name}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
      </div>
      <Textarea aria-label="Text" rows={3} placeholder="Was wurde besprochen?" value={body} onChange={(e) => setBody(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) { e.preventDefault(); e.currentTarget.form?.requestSubmit(); } }} />
      <div className="flex items-center justify-end gap-2">
        <span className="text-xs text-muted-foreground max-sm:hidden">⌘↵ speichert</span>
        <Button type="submit" size="sm" disabled={!body.trim() || add.isPending}>Speichern</Button>
      </div>
    </form>
  );
}

function NoteRow({ companyId, item }: { companyId: number; item: Extract<TimelineItem, { type: "note" }> }) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(item.body);
  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["company", companyId] });
    qc.invalidateQueries({ queryKey: ["companies"] });
    qc.invalidateQueries({ queryKey: ["dashboard"] });
  };
  const save = useMutation({ mutationFn: () => patch<Note>(`/api/notes/${item.id}`, { body: text }), onSuccess: () => { setEditing(false); refresh(); }, onError: (e) => toast.error(e.message) });
  const remove = useMutation({ mutationFn: () => del<{ deleted: number }>(`/api/notes/${item.id}`), onSuccess: refresh, onError: (e) => toast.error(e.message) });
  const Icon = KIND[item.kind].icon;
  return (
    <li className="group flex gap-3">
      <Icon aria-hidden className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
      <div className="min-w-0 flex-1">
        <p className="text-xs text-muted-foreground">{KIND[item.kind].label}{item.contact && ` · ${item.contact.name}`}</p>
        {editing ? (
          <form className="mt-1 space-y-2" onSubmit={(e) => { e.preventDefault(); if (text.trim()) save.mutate(); }}>
            <Textarea rows={3} value={text} onChange={(e) => setText(e.target.value)} autoFocus
              onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) { e.preventDefault(); e.currentTarget.form?.requestSubmit(); } if (e.key === "Escape") setEditing(false); }} />
            <div className="flex gap-2"><Button type="submit" size="xs" disabled={save.isPending}>Speichern</Button><Button type="button" size="xs" variant="ghost" onClick={() => setEditing(false)}>Abbrechen</Button></div>
          </form>
        ) : <p className="text-sm whitespace-pre-wrap">{item.body}</p>}
      </div>
      {!editing && (
        <div className="flex shrink-0 gap-1 opacity-100 transition-opacity duration-150 md:opacity-0 md:group-hover:opacity-100 md:group-focus-within:opacity-100">
          <Button variant="ghost" size="xs" onClick={() => { setText(item.body); setEditing(true); }}>Bearbeiten</Button>
          <Button variant="ghost" size="xs" className="text-destructive hover:text-destructive" onClick={() => confirm("Eintrag löschen?") && remove.mutate()}>Löschen</Button>
        </div>
      )}
    </li>
  );
}

/** The subject links into HEY; a URL that isn't HEY's web app is shown as plain text. */
function MailSubject({ url, subject }: { url: string; subject: string }) {
  const href = heyLink(url);
  const label = subject.trim() || "(kein Betreff)";
  return href
    ? <a href={href} target="_blank" rel="noreferrer" className="font-medium break-words hover:underline">{label}</a>
    : <span className="font-medium break-words">{label}</span>;
}

export function Timeline({ companyId, contacts, items, hasMore, loadingMore, onMore }: {
  companyId: number; contacts: Contact[]; items: TimelineItem[]; hasMore: boolean; loadingMore: boolean; onMore: () => void;
}) {
  const now = today();
  return (
    <section aria-labelledby="timeline-title" className="space-y-4">
      <h2 id="timeline-title" className="text-base font-semibold">Verlauf</h2>
      <Composer companyId={companyId} contacts={contacts} />
      {items.length === 0 && <p className="text-sm text-muted-foreground">Noch nichts passiert.</p>}
      <ol className="space-y-5">
        {groupByDate(items).map((g) => (
          <li key={g.date} className="space-y-2">
            <h3 className="text-xs font-medium text-muted-foreground tabular-nums">{date(g.date)} · {relativeDays(g.date, now)}</h3>
            <ul className="space-y-3">
              {g.items.map((it, i) =>
                it.type === "note" ? <NoteRow key={`n${it.id}`} companyId={companyId} item={it} />
                : it.type === "invoice" ? (
                  <li key={`i${i}`} className="flex gap-3 text-sm">
                    <FileText aria-hidden className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
                    <span>
                      {/* Without a number the word "Rechnung" is the label (and the link), never an empty link. */}
                      {it.number === null
                        ? it.url ? <a href={it.url} target="_blank" rel="noreferrer" className="font-medium hover:underline">Rechnung</a> : "Rechnung"
                        : <>Rechnung {it.url ? <a href={it.url} target="_blank" rel="noreferrer" className="font-medium hover:underline">{it.number}</a> : it.number}</>}
                      {" · "}<span className="tabular-nums">{eur(it.totalEur)}</span>
                    </span>
                  </li>
                ) : it.type === "bookings" ? (
                  <li key={`b${i}`} className="flex gap-3 text-sm text-muted-foreground">
                    <Clock aria-hidden className="mt-0.5 size-4 shrink-0" />
                    <Link to={`/buchungen?client=${companyId}&month=${it.date.slice(0, 7)}&status=all`} className="hover:text-foreground tabular-nums">
                      {hours(it.minutes)} · {it.count} {it.count === 1 ? "Buchung" : "Buchungen"} · {eur(it.amountEur)}
                    </Link>
                  </li>
                ) : (
                  <li key={`m${it.topicId}`} className="flex gap-3 text-sm">
                    <Mail aria-hidden className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
                    <span className="min-w-0">
                      <MailSubject url={it.appUrl} subject={it.subject} />
                      <span className="block text-xs text-muted-foreground tabular-nums">{mailMeta(it)}</span>
                    </span>
                  </li>
                ))}
            </ul>
          </li>
        ))}
      </ol>
      {hasMore && <Button variant="outline" size="sm" disabled={loadingMore} onClick={onMore}>Ältere laden</Button>}
    </section>
  );
}
